import { useEffect, useState } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useDiagnostic } from '@/contexts/DiagnosticContext';
import { diagnosticApi } from '@/services/diagnosticApi';
import { ScoreGauge } from '@/domains/shared/components/ScoreGauge';
import { RadarChart } from '@/domains/shared/components/RadarChart';
import { CategoryBar } from '@/domains/shared/components/CategoryBar';
import { Card, CardTitle, CardContent } from '@/domains/shared/components/Card';
import { Alert } from '@/domains/shared/components/Alert';
import { Button } from '@/domains/shared/components/Button';
import { Toast } from '@/domains/shared/components/Toast';
import { DIAGNOSTIC_CATEGORIES } from '@/domains/assessment/constants';
import type { CategoryKey } from '@/domains/assessment/types';

export function ResultPage() {
  const { navigate, params } = useRouter();
  const { result, setResult } = useDiagnostic();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Extrai token da query string da URL (seja via roteador ou window.location)
  const tokenFromUrl =
    params.token ||
    (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null);

  // Hidratação por URL: caso haja token e o resultado local não corresponda a ele
  useEffect(() => {
    if (!tokenFromUrl) return;

    if (result && result.publicToken === tokenFromUrl) return;

    let isMounted = true;
    setIsLoading(true);
    setLoadError(null);

    diagnosticApi
      .getPublicResult(tokenFromUrl)
      .then((data) => {
        if (isMounted) {
          setResult(data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setLoadError(
            err instanceof Error ? err.message : 'Não foi possível carregar o diagnóstico informado.',
          );
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tokenFromUrl, result, setResult]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 text-center text-brand-offWhite">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-dark p-8 shadow-2xl">
          <div className="relative mx-auto flex h-16 w-16 items-center justify-center">
            <div className="absolute h-full w-full animate-spin rounded-full border-4 border-white/10 border-t-brand-orange" />
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-orange text-xs font-black text-white">
              IC
            </div>
          </div>
          <h2 className="mt-5 text-lg font-bold text-white">Carregando seu diagnóstico...</h2>
          <p className="mt-2 text-xs text-brand-offWhite/70">
            Buscando as notas e prioridades estratégicas com base no seu token de consulta.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 text-center text-brand-offWhite">
        <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-navy-dark p-8 shadow-2xl">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 text-red-400">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-bold text-white">Diagnóstico não encontrado</h2>
          <p className="mt-2 text-xs text-brand-offWhite/80 leading-relaxed">{loadError}</p>
          <div className="mt-6">
            <Button variant="primary" onClick={() => navigate('/')} className="w-full">
              Iniciar Novo Diagnóstico
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy p-4 text-center">
        <div className="max-w-md rounded-2xl border border-white/10 bg-navy-dark p-8 shadow-2xl">
          <p className="text-white mb-4 text-sm">Nenhum diagnóstico encontrado nesta sessão.</p>
          <Button onClick={() => navigate('/')} className="w-full">
            Iniciar Diagnóstico Gratuito
          </Button>
        </div>
      </div>
    );
  }

  // Mensagem contextual para o WhatsApp
  const whatsappMessage = `Olá Israel! Acabei de fazer o Diagnóstico de Presença Estratégica da minha contabilidade. Tirei nota ${result.overallScore}% (${result.overallTier.label}) e minha prioridade principal foi "${result.primaryPriority.name}". Quero conversar sobre como dar o próximo passo!`;

  const handleOpenWhatsApp = () => {
    const officialNumber = import.meta.env.VITE_WHATSAPP_NUMBER || '5581992451821';
    const cleanNumber = officialNumber.replace(/\D/g, '');

    // Sanitiza qualquer URL legada para garantir estritamente o número oficial de Pernambuco (81)
    let url = result.whatsappUrl;
    if (url) {
      url = url.replace(/wa\.me\/\d+/, `wa.me/${cleanNumber}`);
    } else {
      url = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(whatsappMessage)}`;
    }

    window.open(url, '_blank');
    setToastMessage('Redirecionando para o WhatsApp comercial com mensagem contextualizada!');
  };

  const handleCopyLink = () => {
    const shareUrl =
      typeof window !== 'undefined' && result.publicToken
        ? `${window.location.origin}/resultado?token=${result.publicToken}`
        : window.location.href;
    navigator.clipboard?.writeText(shareUrl);
    setToastMessage('Link de consulta copiado para a área de transferência!');
  };

  const getPriorityExplanation = (key: CategoryKey): string => {
    switch (key) {
      case 'positioning':
        return 'Seu escritório precisa deixar claro para quem resolve problemas de alto valor. Sem posicionamento nítido, todo esforço de atração acaba brigando por preço.';
      case 'commercial_conversation':
        return 'Seu principal vazamento está no momento do fechamento. Antes de enviar tabela de honorários, é fundamental conduzir o diagnóstico do risco e gerar percepção de valor.';
      case 'distribution':
        return 'Sua mensagem ainda chega a poucas pessoas. É hora de diversificar canais, produzir pautas ligadas às dores reais dos clientes e estruturar parcerias.';
      case 'founder_authority':
        return 'Contabilidade é um negócio de confiança e pessoas. O fundador precisa aparecer com consistência como porta-voz do método e educador do empresário.';
      case 'digital_assets':
        return 'Seu perfil e site não estão prontos para receber tráfego qualificado. Arrume a bio, proposta de valor e facilite o canal direto de contato.';
      case 'customer_experience':
        return 'Atenção imediata: o que é prometido nas vendas não está sendo sustentado com clareza no atendimento diário, gerando desgaste e risco de perda de clientes.';
      default:
        return 'Foque neste ponto para destravar o crescimento do seu escritório.';
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      {/* Header com token de consulta */}
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-orange text-xs font-bold text-white">
              IC
            </div>
            <span className="text-xs font-bold text-white sm:text-sm">
              Devolutiva do Diagnóstico
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopyLink}
            >
              Copiar Link
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-4 py-8 sm:py-12">
        {/* Saudação e Nível Geral */}
        <div className="text-center">
          <span className="inline-block rounded-full bg-brand-orange/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange">
            Diagnóstico Finalizado
          </span>
          <h1 className="mt-3 text-2xl font-black text-white sm:text-4xl">
            {result.leadFirstName}, aqui está o mapa de presença do seu escritório
          </h1>
          <p className="mt-2 text-sm text-brand-offWhite/80 max-w-xl mx-auto">
            Calculamos sua nota geral e o equilíbrio das suas 6 dimensões. Veja onde você já tem força e onde precisa concentrar energia agora.
          </p>
        </div>

        {/* Alerta de Risco de Retenção (se aplicável) */}
        {result.hasRetentionRisk && (
          <div className="mt-8">
            <Alert
              variant="danger"
              title="Atenção Crítica: Risco de Retenção de Clientes Detectado"
            >
              Sua nota na dimensão <strong>Experiência do Cliente</strong> ficou abaixo de 40%. Não adianta atrair novos contratos se o atendimento vaza clientes pela porta dos fundos. Ajuste a rotina de alinhamento de expectativas antes de acelerar novos investimentos.
            </Alert>
          </div>
        )}

        {/* Bloco Central: Nota Geral + Gráfico Radar */}
        <div className="mt-10 grid grid-cols-1 items-center gap-8 rounded-2xl border border-white/10 bg-navy-light/30 p-4 sm:p-6 md:p-8 md:grid-cols-2 min-w-0">
          {/* Lado Esquerdo: Score Gauge */}
          <div className="flex flex-col items-center justify-center text-center min-w-0 w-full">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-offWhite/60 mb-2">
              Maturidade Estratégica Geral
            </span>
            <ScoreGauge score={result.overallScore} tier={result.overallTier} size="lg" />
            <p className="mt-4 text-xs text-brand-offWhite/70 max-w-xs">
              Sua nota resume as 18 respostas deste diagnóstico.
            </p>
          </div>

          {/* Lado Direito: Radar Chart com alternativa em tabela */}
          <div className="flex flex-col items-center justify-center min-w-0 w-full">
            <RadarChart scores={result.categoryScores} className="w-full" />
          </div>
        </div>

        {/* Prioridade Principal Recomendada */}
        <section className="mt-10 text-left" aria-labelledby="prioridade-title">
          <h2 id="prioridade-title" className="text-xl font-bold text-white sm:text-2xl mb-4">
            Seu Movimento Imediato
          </h2>

          <Card variant="highlight" className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="inline-block rounded bg-brand-orange px-2.5 py-0.5 text-xs font-black uppercase tracking-wider text-white">
                  Prioridade 01: {result.primaryPriority.name}
                </span>
                <CardTitle className="mt-2 text-xl text-white">
                  O que você deve arrumar primeiro
                </CardTitle>
              </div>
              <span className="text-2xl font-black text-brand-orange">
                {result.categoryScores[result.primaryPriority.key]}%
              </span>
            </div>

            <CardContent className="mt-4 text-sm text-brand-offWhite/90 leading-relaxed">
              <p>{getPriorityExplanation(result.primaryPriority.key)}</p>
            </CardContent>
          </Card>

          {/* Prioridade Secundária (se houver) */}
          {result.secondaryPriority && (
            <Card variant="default" className="mt-4 p-5 bg-navy-light/40">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-orangeSecondary">
                  Prioridade 02: {result.secondaryPriority.name} ({result.categoryScores[result.secondaryPriority.key]}%)
                </span>
                <span className="text-xs text-brand-offWhite/60">Próximo passo</span>
              </div>
              <p className="mt-2 text-xs text-brand-offWhite/80 leading-relaxed">
                {getPriorityExplanation(result.secondaryPriority.key)}
              </p>
            </Card>
          )}
        </section>

        {/* Detalhamento das 6 Dimensões */}
        <section className="mt-12 text-left" aria-labelledby="dimensoes-detail-title">
          <h2 id="dimensoes-detail-title" className="text-xl font-bold text-white sm:text-2xl mb-4">
            Notas por Dimensão Estratégica
          </h2>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DIAGNOSTIC_CATEGORIES.map((cat) => (
              <CategoryBar
                key={cat.key}
                categoryKey={cat.key}
                name={cat.name}
                score={result.categoryScores[cat.key]}
                isPrimaryPriority={result.primaryPriority.key === cat.key}
                isSecondaryPriority={result.secondaryPriority?.key === cat.key}
                hasRetentionRisk={cat.key === 'customer_experience' && result.hasRetentionRisk}
              />
            ))}
          </div>
        </section>

        {/* CTA Contextual para WhatsApp */}
        <section className="mt-12 rounded-2xl border-2 border-brand-orange/60 bg-gradient-to-b from-brand-orange/15 to-navy-light p-6 sm:p-8 text-center">
          <span className="inline-block rounded-full bg-brand-orange px-3 py-1 text-xs font-black uppercase tracking-wider text-white mb-3">
            Atendimento Contextualizado
          </span>

          <h2 className="text-2xl font-black text-white sm:text-3xl">
            Bora conversar sobre o plano do seu escritório?
          </h2>

          <p className="mt-3 text-sm text-brand-offWhite/80 max-w-xl mx-auto">
            Envie sua nota diretamente para o WhatsApp de Israel Costa e time. Nós já sabemos exatamente qual dimensão precisa de alinhamento e vamos direto ao ponto.
          </p>

          {/* Pré-visualização da mensagem */}
          <div className="mt-5 mx-auto max-w-lg rounded-xl border border-white/10 bg-navy/80 p-3.5 text-left text-xs text-brand-offWhite/80">
            <span className="block font-bold text-brand-orange mb-1">
              Mensagem pré-formatada que será enviada:
            </span>
            <p className="italic">"{whatsappMessage}"</p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={handleOpenWhatsApp}
              className="text-base font-black px-8 py-4 shadow-xl shadow-brand-orange/30 flex items-center justify-center gap-2"
            >
              <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
              Iniciar Conversa no WhatsApp →
            </Button>
          </div>
        </section>

        {/* Rodapé de navegação do resultado */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-brand-offWhite/60">
          <span>Versão do Algoritmo: {result.algorithmVersion}</span>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="hover:text-brand-orange underline"
          >
            Refazer Diagnóstico com Outras Respostas
          </button>
        </div>
      </main>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
